import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { ScrollArea } from './components/ui/scroll-area';
import { Separator } from './components/ui/separator';
import { toast } from 'sonner';
import { Lock, LogOut, Mail, Eye, EyeOff, FileText, Send } from 'lucide-react';
import './App.css';
import CountdownReport from './components/CountdownReport';
import PaidLockedTable from './components/PaidLockedTable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getAuthHeader() {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function authAxios() {
  return axios.create({
    baseURL: API_URL,
    headers: getAuthHeader()
  });
}

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/login`, { email, password });
      localStorage.setItem('admin_token', data.token);
      onLogin(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#24A1DE]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-[#24A1DE]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Admin Login</h1>
          <p className="text-sm text-gray-500">Instant Email Bot Dashboard</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@telegramemailbot.com"
                className="pl-10 bg-gray-900 border-gray-800 text-white placeholder:text-gray-600"
                required
                data-testid="admin-email-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="pl-10 pr-10 bg-gray-900 border-gray-800 text-white placeholder:text-gray-600"
                required
                data-testid="admin-password-input"
              />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="admin-login-error">{error}</p>}
          <Button type="submit" className="w-full bg-[#24A1DE] hover:bg-[#1d8abf] text-white" disabled={loading} data-testid="admin-login-submit">
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [stats, setStats] = useState({ total_users: 0, total_emails_sent: 0, total_emails_received: 0, active_today: 0 });
  const [health, setHealth] = useState(null);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [emails, setEmails] = useState([]);
  const [emailSearchQuery, setEmailSearchQuery] = useState('');
  const [emailSearchLoading, setEmailSearchLoading] = useState(false);
  const [emailSearchActive, setEmailSearchActive] = useState(false);
  const [usersData, setUsersData] = useState({ free: [], monthly: [], yearly: [], totals: {} });
  const [gmailStatus, setGmailStatus] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [affiliateData, setAffiliateData] = useState({ affiliates: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [userCategory, setUserCategory] = useState('all');
  const [extendDays, setExtendDays] = useState(30);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState(null); // null = idle, [] = no matches
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [sendingTestFor, setSendingTestFor] = useState(null); // chat_id currently sending
  const [winBack, setWinBack] = useState(null);
  const [winBackLoading, setWinBackLoading] = useState(false);
  const [nudgingFor, setNudgingFor] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);

  const handleAuthError = useCallback((error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      onLogout();
      toast.error('Session expired. Please log in again.');
    }
  }, [onLogout]);

  const fetchWinBack = useCallback(async () => {
    setWinBackLoading(true);
    try {
      const res = await authAxios().get('/api/admin/win-back');
      setWinBack(res.data);
    } catch (e) {
      handleAuthError(e);
      toast.error('Failed to load win-back list');
    }
    setWinBackLoading(false);
  }, [handleAuthError]);

  useEffect(() => {
    if (activeTab === 'winback' && winBack === null && !winBackLoading) fetchWinBack();
  }, [activeTab, winBack, winBackLoading, fetchWinBack]);

  const fetchRevenue = useCallback(async () => {
    setRevenueLoading(true);
    try {
      const res = await authAxios().get('/api/admin/revenue-diagnostic');
      setRevenue(res.data);
    } catch (e) {
      handleAuthError(e);
      toast.error('Failed to load revenue diagnostic');
    }
    setRevenueLoading(false);
  }, [handleAuthError]);

  useEffect(() => {
    if (activeTab === 'revenue' && revenue === null && !revenueLoading) fetchRevenue();
  }, [activeTab, revenue, revenueLoading, fetchRevenue]);

  const sendWinBackNudge = async (chatId) => {
    setNudgingFor(chatId);
    try {
      const res = await authAxios().post(`/api/admin/win-back/nudge/${chatId}`, {});
      if (res.data?.success) {
        toast.success(`Nudge sent to ${res.data.email_address}`);
        setWinBack((w) => w ? { ...w, users: w.users.map(u => u.chat_id === chatId ? { ...u, nudged_at: new Date().toISOString() } : u) } : w);
      } else {
        toast.error(`Could not send: ${res.data?.error || 'unknown error'}`);
      }
    } catch (e) {
      handleAuthError(e);
      toast.error('Failed to send nudge');
    }
    setNudgingFor(null);
  };

  const nudgeAll = async () => {
    if (!window.confirm('Send a win-back nudge to all lapsed users? (Anyone nudged in the last 3 days is skipped.)')) return;
    setNudgingFor('ALL');
    try {
      const res = await authAxios().post('/api/admin/win-back/nudge-all', {});
      const d = res.data || {};
      toast.success(`Sent ${d.sent} · skipped ${d.skipped} · failed ${d.failed}`);
      fetchWinBack();
    } catch (e) {
      handleAuthError(e);
      toast.error('Failed to run bulk nudge');
    }
    setNudgingFor(null);
  };


  const fetchData = useCallback(async () => {
    setLoading(true);
    const api = authAxios();
    try {
      const [statsRes, healthRes, webhookRes, emailsRes, usersRes, gmailRes, billingRes, paymentsRes, affiliatesRes, affiliateSummaryRes] = await Promise.all([
        api.get('/api/stats'),
        axios.get(`${API_URL}/api/health`),
        api.get('/api/telegram/webhook-info'),
        api.get('/api/emails?limit=50'),
        api.get('/api/admin/users-with-subscriptions'),
        api.get('/api/gmail/status'),
        api.get('/api/admin/billing-summary'),
        api.get('/api/admin/payments'),
        api.get('/api/admin/affiliates'),
        api.get('/api/admin/affiliate-summary')
      ]);
      setStats(statsRes.data);
      setHealth(healthRes.data);
      setWebhookInfo(webhookRes.data);
      setEmails(emailsRes.data.emails || []);
      setUsersData(usersRes.data);
      setGmailStatus(gmailRes.data);
      setBillingData(billingRes.data);
      setPayments(paymentsRes.data.payments || []);
      setAffiliateData({ affiliates: affiliatesRes.data.affiliates || [], summary: affiliateSummaryRes.data });
    } catch (error) {
      handleAuthError(error);
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    }
    setLoading(false);
  }, [handleAuthError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const apiCall = async (method, url, data) => {
    const api = authAxios();
    try {
      const res = method === 'post' ? await api.post(url, data) : await api.get(url);
      return res;
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  };

  const runEmailSearch = async () => {
    const q = emailSearchQuery.trim();
    if (!q) { setEmailSearchActive(false); fetchData(); return; }
    setEmailSearchLoading(true);
    try {
      const res = await apiCall('get', `/api/emails?q=${encodeURIComponent(q)}&limit=200`);
      setEmails(res.data.emails || []);
      setEmailSearchActive(true);
      toast.success(`Found ${res.data.emails?.length || 0} email(s)`);
    } catch {
      toast.error('Email search failed');
    }
    setEmailSearchLoading(false);
  };

  const clearEmailSearch = () => {
    setEmailSearchQuery('');
    setEmailSearchActive(false);
    fetchData();
  };

  const runUserSearch = async () => {
    const q = userSearchQuery.trim();
    if (!q) {
      setUserSearchResults(null);
      return;
    }
    setUserSearchLoading(true);
    try {
      const res = await apiCall('get', `/api/admin/user-search?q=${encodeURIComponent(q)}`);
      setUserSearchResults(res.data.matches || []);
      if ((res.data.matches || []).length === 0) {
        toast.error(`No user found for "${q}"`);
      } else {
        toast.success(`Found ${res.data.count} match(es) via ${res.data.source}`);
      }
    } catch (error) {
      console.error('User search failed:', error);
      toast.error('User search failed');
      setUserSearchResults([]);
    }
    setUserSearchLoading(false);
  };

  const clearUserSearch = () => {
    setUserSearchQuery('');
    setUserSearchResults(null);
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    try {
      navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (_e) {
      toast.error('Copy failed');
    }
  };

  // Map effective_plan_type from backend -> UI badge label + className
  const getPlanBadge = (user) => {
    const plan = user?.effective_plan_type || user?.plan_status?.plan_type;
    if (plan === 'subscriber')          return { label: 'Yearly Subscriber', className: 'badge-yearly' };
    if (plan === 'starter')             return { label: 'Starter (Paid)',    className: 'badge-monthly' };
    if (plan === 'starter_exhausted')   return { label: 'Starter — Cap Reached', className: 'badge-monthly' };
    if (plan === 'trial')               return { label: 'Trial (Legacy)',    className: 'badge-free' };
    if (plan === 'expired')             return { label: 'Expired',           className: 'badge-free' };
    if (plan === 'new')                 return { label: 'Never Paid',        className: 'badge-free' };
    // Fallback to legacy shape
    return {
      label: user?.subscription_active ? (user?.subscription?.plan === 'yearly' ? 'Yearly' : 'Active') : 'Free/Expired',
      className: user?.subscription_active ? (user?.subscription?.plan === 'yearly' ? 'badge-yearly' : 'badge-monthly') : 'badge-free',
    };
  };

  // Returns "X of 20 emails used" string for Starter tiers, else null
  const getStarterUsageLabel = (user) => {
    const plan = user?.effective_plan_type || user?.plan_status?.plan_type;
    if (plan !== 'starter' && plan !== 'starter_exhausted') return null;
    const used = user?.plan_status?.starter_emails_used;
    const limit = user?.plan_status?.starter_emails_limit;
    if (used === undefined || limit === undefined) return null;
    return `${used} of ${limit} emails used`;
  };

  const sendTestEmailTo = async (user) => {
    if (!user?.chat_id) return;
    if (!window.confirm(`Send a diagnostic test email to ${user.email_address}?\n\nIt should land in the Telegram chat within ~30-60 seconds if the pipeline is healthy.`)) return;
    setSendingTestFor(user.chat_id);
    try {
      const res = await apiCall('post', `/api/admin/user/${user.chat_id}/send-test-email`);
      if (res.data.success) {
        toast.success(`Test email sent to ${res.data.to_email}. Watch Telegram in ~30-60s.`);
      } else {
        toast.error('Test email dispatch reported no success');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to send test email');
      toast.error(msg);
    }
    setSendingTestFor(null);
  };

  const [adjustingFor, setAdjustingFor] = useState(null); // "chat_id:action" while running

  const runAdminAction = async (user, action, endpoint, promptMsg, successMsg) => {
    if (!user?.chat_id) return;
    if (!window.confirm(promptMsg)) return;
    const key = `${user.chat_id}:${action}`;
    setAdjustingFor(key);
    try {
      const res = await apiCall('post', endpoint);
      if (res.data.success) {
        toast.success(successMsg);
        // Refresh the current search so the badge/usage updates immediately
        if (userSearchResults !== null && userSearchQuery.trim()) {
          const r2 = await apiCall('get', `/api/admin/user-search?q=${encodeURIComponent(userSearchQuery.trim())}`);
          setUserSearchResults(r2.data.matches || []);
        }
        fetchData();
      } else {
        toast.error('Action reported no success');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || `Failed to ${action}`);
      toast.error(msg);
    }
    setAdjustingFor(null);
  };

  const markAsStarter = (user) => runAdminAction(
    user, 'mark-starter',
    `/api/admin/user/${user.chat_id}/mark-starter`,
    `Manually mark ${user.first_name || user.username || 'this user'} as Starter Paid?\n\nThis sets plan_type='starter' and resets their 20-email counter to 0.\nUse only when a Stripe payment was received but activation missed.`,
    'User marked as Starter Paid. Their inbox is now active.',
  );

  const grantSubscriber = (user) => runAdminAction(
    user, 'grant-subscriber',
    `/api/admin/user/${user.chat_id}/grant-subscriber?days=365`,
    `Grant ${user.first_name || user.username || 'this user'} a 1-year Subscriber account?\n\nCreates a yearly subscription expiring 365 days from now with unlimited emails + AI summaries.`,
    'Granted 1-year Subscriber. Expires 365 days from now.',
  );

  const extend30Days = (user) => runAdminAction(
    user, 'extend-30',
    `/api/admin/user/${user.chat_id}/extend-subscription?days=30`,
    `Extend ${user.first_name || user.username || 'this user'}'s subscription by 30 days?`,
    'Subscription extended by 30 days.',
  );

  const [paymentLinkFor, setPaymentLinkFor] = useState(null);
  const [paymentLinks, setPaymentLinks] = useState({}); // { chat_id: url }
  const createPaymentLink = async (user) => {
    if (!window.confirm(`Generate a $14.99 / year payment link for ${user.first_name || user.username || 'this user'} (${user.email_address})?\n\nWhen they pay, their yearly subscription activates automatically.`)) return;
    setPaymentLinkFor(user.chat_id);
    try {
      const res = await apiCall('post', `/api/admin/user/${user.chat_id}/payment-link?plan=yearly`);
      const url = res.data?.payment_link;
      if (url) {
        setPaymentLinks((m) => ({ ...m, [user.chat_id]: url }));
        try { await navigator.clipboard.writeText(url); toast.success('Payment link created & copied to clipboard'); }
        catch { toast.success('Payment link created'); }
      } else {
        toast.error('No link returned');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create payment link');
    }
    setPaymentLinkFor(null);
  };

  const setWebhook = async () => {
    if (!webhookUrl) { toast.error('Please enter a webhook URL'); return; }
    try {
      const res = await apiCall('post', '/api/telegram/set-webhook', { webhook_url: webhookUrl });
      if (res.data.ok) { toast.success('Webhook set successfully!'); fetchData(); }
      else toast.error(res.data.description || 'Failed to set webhook');
    } catch { toast.error('Failed to set webhook'); }
  };

  const connectGmail = () => { window.location.href = `${API_URL}/api/oauth/gmail/login`; };

  const disconnectGmail = async () => {
    try { await apiCall('post', '/api/gmail/disconnect'); toast.success('Gmail disconnected'); fetchData(); }
    catch { toast.error('Failed to disconnect Gmail'); }
  };

  const triggerPoll = async () => {
    try { await apiCall('post', '/api/gmail/poll'); toast.success('Gmail poll triggered'); fetchData(); }
    catch { toast.error('Failed to trigger poll'); }
  };

  const activateSubscription = async (chatId, plan) => {
    try { await apiCall('post', '/api/admin/subscription/activate', { chat_id: chatId, plan }); toast.success(`${plan} subscription activated!`); fetchData(); }
    catch { toast.error('Failed to activate subscription'); }
  };

  const giveFreeSubscription = async (chatId, plan) => {
    try { await apiCall('post', '/api/admin/subscription/give-free', { chat_id: chatId, plan, days: extendDays }); toast.success(`Free ${plan} subscription given!`); fetchData(); }
    catch { toast.error('Failed to give free subscription'); }
  };

  const deactivateSubscription = async (chatId) => {
    try { await apiCall('post', '/api/admin/subscription/deactivate', { chat_id: chatId }); toast.success('Subscription deactivated'); fetchData(); }
    catch { toast.error('Failed to deactivate subscription'); }
  };

  const extendSubscription = async (chatId) => {
    try { await apiCall('post', '/api/admin/subscription/extend', { chat_id: chatId, days: extendDays }); toast.success(`Subscription extended by ${extendDays} days`); fetchData(); }
    catch { toast.error('Failed to extend subscription'); }
  };

  const blockUser = async (chatId) => {
    try { await apiCall('post', '/api/admin/block-user', { chat_id: chatId, reason: 'admin_block' }); toast.success(`User ${chatId} blocked`); fetchData(); }
    catch { toast.error('Failed to block user'); }
  };

  const unblockUser = async (chatId) => {
    try { await apiCall('post', '/api/admin/unblock-user', { chat_id: chatId }); toast.success(`User ${chatId} unblocked`); fetchData(); }
    catch { toast.error('Failed to unblock user'); }
  };

  const processAffiliatePayout = async (affiliateId, amount) => {
    try { await apiCall('post', '/api/admin/affiliate/payout', { affiliate_id: affiliateId, amount }); toast.success('Payout processed successfully!'); fetchData(); }
    catch { toast.error('Failed to process payout'); }
  };

  const downloadReceipt = async (chatId) => {
    try {
      const response = await authAxios().get(`/api/admin/receipt/${chatId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Receipt_${chatId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded');
    } catch { toast.error('Failed to generate receipt. User may not have an active subscription.'); }
  };

  const sendReceiptToUser = async (chatId) => {
    try {
      await apiCall('post', `/api/admin/receipt/send/${chatId}`);
      toast.success('Receipt sent to user via Telegram!');
    } catch { toast.error('Failed to send receipt'); }
  };

  const viewReceiptHtml = async (chatId) => {
    try {
      const { data } = await authAxios().get(`/api/admin/receipt-html/${chatId}`);
      const win = window.open('', '_blank');
      win.document.write(`<!DOCTYPE html><html><head><title>Receipt ${data.receipt_number}</title>
        <style>
          * { margin:0; padding:0; box-sizing:border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; background:#f5f5f5; padding:40px; }
          .receipt { max-width:700px; margin:0 auto; background:#fff; border-radius:12px; box-shadow:0 2px 20px rgba(0,0,0,0.08); overflow:hidden; }
          .header { background:#0088CC; color:#fff; padding:30px 40px; display:flex; justify-content:space-between; align-items:flex-start; }
          .header h1 { font-size:24px; margin-bottom:4px; }
          .header-right { text-align:right; }
          .header small { opacity:0.8; font-size:13px; }
          .body { padding:30px 40px; }
          .row { display:flex; justify-content:space-between; gap:30px; margin-bottom:24px; }
          .box { flex:1; background:#f9fafb; border-radius:8px; padding:16px; }
          .box h4 { font-size:11px; color:#888; text-transform:uppercase; letter-spacing:1px; margin-bottom:8px; }
          .box p { font-size:13px; color:#333; margin:3px 0; }
          table { width:100%; border-collapse:collapse; margin:20px 0; }
          th { background:#0088CC; color:#fff; padding:10px 16px; text-align:left; font-size:12px; }
          th:last-child { text-align:right; }
          td { padding:12px 16px; border-bottom:1px solid #eee; font-size:13px; color:#333; }
          td:last-child { text-align:right; font-weight:700; }
          .totals { text-align:right; margin-top:10px; }
          .totals .line { display:flex; justify-content:flex-end; gap:40px; padding:4px 0; font-size:13px; color:#666; }
          .totals .total { font-size:18px; font-weight:800; color:#0088CC; border-top:2px solid #0088CC; padding-top:8px; margin-top:8px; }
          .payment-info { background:#f9fafb; border-radius:8px; padding:14px 16px; margin-top:20px; font-size:12px; color:#888; }
          .footer { text-align:center; padding:20px 40px; color:#999; font-size:11px; border-top:1px solid #eee; }
          @media print { body { padding:0; background:#fff; } .receipt { box-shadow:none; } }
          .print-btn { display:block; margin:20px auto; background:#0088CC; color:#fff; border:none; padding:10px 30px; border-radius:6px; font-size:14px; cursor:pointer; }
          @media print { .print-btn { display:none; } }
        </style>
      </head><body>
        <button class="print-btn" onclick="window.print()">Print Receipt</button>
        <div class="receipt">
          <div class="header">
            <div><h1>TelegramEmailBot</h1><small>Instant Email in Telegram</small></div>
            <div class="header-right"><strong>RECEIPT</strong><br/><small>#${data.receipt_number}</small><br/><small>${data.date}</small></div>
          </div>
          <div class="body">
            <div class="row">
              <div class="box"><h4>From</h4><p><strong>Mii Global Partner Holdings Sdn. Bhd.</strong></p><p>202601028203 (1690298-K)</p><p>17, Taman Bukit Dumbar, 11600, Jelutong, Penang</p><p>miiglobalpartner@gmail.com</p></div>
              <div class="box"><h4>Bill To</h4><p><strong>${data.customer_name}</strong></p><p>${data.customer_email}</p><p>Chat ID: ${data.customer_chat_id}</p></div>
            </div>
            <table><thead><tr><th>Description</th><th>Period</th><th>Amount</th></tr></thead>
              <tbody><tr><td>TelegramEmailBot — ${data.plan}</td><td>${data.period_label || data.period_start || ''}</td><td>$${data.amount} ${data.currency}</td></tr></tbody>
            </table>
            <div class="totals">
              <div class="line"><span>Subtotal:</span><span>$${data.amount}</span></div>
              <div class="line"><span>Tax:</span><span>$0.00</span></div>
              <div class="line total"><span>Total:</span><span>$${data.amount} ${data.currency}</span></div>
            </div>
            <div class="payment-info">Payment Method: ${data.payment_method} | Transaction: ${data.session_id}</div>
          </div>
          <div class="footer">Thank you for your business!<br/>Mii Global Partner Holdings Sdn. Bhd. | 202601028203 (1690298-K) | miiglobalpartner@gmail.com<br/>17, Taman Bukit Dumbar, 11600, Jelutong, Penang, Malaysia</div>
        </div>
      </body></html>`);
      win.document.close();
    } catch { toast.error('Failed to load receipt data'); }
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleString() : 'N/A';
  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const getAllUsers = () => {
    if (userCategory === 'free') return usersData.free || [];
    if (userCategory === 'monthly') return usersData.monthly || [];
    if (userCategory === 'yearly') return usersData.yearly || [];
    return [...(usersData.free || []), ...(usersData.monthly || []), ...(usersData.yearly || [])];
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    onLogout();
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 5L12 13L3 5" />
                <rect x="3" y="5" width="18" height="14" rx="2" />
              </svg>
            </div>
            <div>
              <h1 className="logo-title">Instant Email Bot</h1>
              <p className="logo-subtitle">Admin Dashboard</p>
            </div>
          </div>
          <div className="status-badges">
            {health && (
              <>
                <Badge variant={health.telegram_configured ? "default" : "destructive"} className={health.telegram_configured ? "badge-success" : ""}>
                  Telegram {health.telegram_configured ? 'Connected' : 'Not Configured'}
                </Badge>
                <Badge variant={health.brevo_configured ? "default" : "secondary"} className={health.brevo_configured ? "badge-success" : "badge-warning"}>
                  Brevo {health.brevo_configured ? 'Connected' : 'Pending'}
                </Badge>
                <Badge variant={health.gmail_connected ? "default" : "secondary"} className={health.gmail_connected ? "badge-success" : "badge-warning"}>
                  Gmail {health.gmail_connected ? 'Connected' : 'Pending'}
                </Badge>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-400 hover:text-white ml-2" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <nav className="app-nav">
        {['dashboard', 'subscriptions', 'billing', 'affiliates', 'users', 'winback', 'revenue', 'emails', 'setup'].map(tab => (
          <button key={tab} className={`nav-btn ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)} data-testid={`nav-${tab}`}>
            {tab === 'winback' ? 'Win-Back' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </nav>

      <main className="app-main">
        {loading ? (
          <div className="loading-container"><div className="loading-spinner"></div><p>Loading...</p></div>
        ) : (
          <>
            {activeTab === 'dashboard' && (
              <div className="dashboard-grid">
                <Card className="stat-card" data-testid="stat-total-users">
                  <CardHeader className="stat-header">
                    <CardDescription>Total Users</CardDescription>
                    <CardTitle className="stat-value">{usersData.totals?.total || 0}</CardTitle>
                  </CardHeader>
                </Card>
                <Card className="stat-card" data-testid="stat-active-subs">
                  <CardHeader className="stat-header">
                    <CardDescription>Active Subscriptions</CardDescription>
                    <CardTitle className="stat-value">{billingData?.active_subscriptions?.total || 0}</CardTitle>
                  </CardHeader>
                </Card>
                <Card className="stat-card" data-testid="stat-emails-sent">
                  <CardHeader className="stat-header">
                    <CardDescription>Emails Sent</CardDescription>
                    <CardTitle className="stat-value">{stats.total_emails_sent}</CardTitle>
                  </CardHeader>
                </Card>
                <Card className="stat-card" data-testid="stat-mrr">
                  <CardHeader className="stat-header">
                    <CardDescription>Monthly Revenue</CardDescription>
                    <CardTitle className="stat-value">{formatCurrency(billingData?.mrr || 0)}</CardTitle>
                  </CardHeader>
                </Card>
                <Card className="info-card full-width">
                  <CardHeader><CardTitle>User Categories</CardTitle></CardHeader>
                  <CardContent>
                    <div className="category-grid">
                      <div className="category-card free">
                        <div className="category-info">
                          <span className="category-count">{usersData.totals?.free || 0}</span>
                          <span className="category-label">Free Users</span>
                        </div>
                      </div>
                      <div className="category-card monthly">
                        <div className="category-info">
                          <span className="category-count">{usersData.totals?.monthly || 0}</span>
                          <span className="category-label">Monthly</span>
                        </div>
                      </div>
                      <div className="category-card yearly">
                        <div className="category-info">
                          <span className="category-count">{usersData.totals?.yearly || 0}</span>
                          <span className="category-label">Yearly</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'subscriptions' && (
              <div className="subscriptions-container">
                <Card className="list-card">
                  <CardHeader>
                    <div className="card-header-flex">
                      <div>
                        <CardTitle>Subscription Management</CardTitle>
                        <CardDescription>Manage user subscriptions</CardDescription>
                      </div>
                      <div className="filter-buttons">
                        {['all', 'free', 'monthly', 'yearly'].map(cat => (
                          <Button key={cat} variant={userCategory === cat ? 'default' : 'outline'} size="sm" onClick={() => setUserCategory(cat)}>
                            {cat.charAt(0).toUpperCase() + cat.slice(1)} ({usersData.totals?.[cat] || usersData.totals?.total || 0})
                          </Button>
                        ))}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="subscription-list-scroll">
                      {getAllUsers().length === 0 ? (
                        <p className="empty-state">No users in this category</p>
                      ) : (
                        <div className="subscription-list">
                          {getAllUsers().map((user, index) => (
                            <div key={user.id || index} className="subscription-item" data-testid={`sub-item-${index}`}>
                              <div className="sub-user-info">
                                <div className="user-avatar">{(user.first_name || user.username || 'U')[0].toUpperCase()}</div>
                                <div className="sub-details">
                                  <p className="user-name">
                                    {user.first_name || user.username || 'Unknown'}
                                  </p>
                                  {user.is_blocked && <Badge className="bg-red-600 text-white text-[10px]" data-testid={`blocked-badge-${index}`}>BLOCKED</Badge>}
                                  <p className="user-email">{user.email_address}</p>
                                  <p className="user-meta-small">Chat ID: {user.chat_id} | Emails: {user.email_count || 0}</p>
                                </div>
                              </div>
                              <div className="sub-status">
                                {user.subscription_active ? (
                                  <>
                                    <Badge className={user.subscription?.plan === 'yearly' ? 'badge-yearly' : 'badge-monthly'}>
                                      {user.subscription?.plan === 'yearly' ? 'Yearly' : 'Monthly'}
                                    </Badge>
                                    <span className="expiry-date">Expires: {formatDate(user.subscription?.expires_at).split(',')[0]}</span>
                                  </>
                                ) : (
                                  <Badge className="badge-free">Free</Badge>
                                )}
                              </div>
                              <div className="sub-actions">
                                {user.is_blocked ? (
                                  <Button size="sm" variant="outline" className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white" onClick={() => unblockUser(user.chat_id)} data-testid={`unblock-btn-${index}`}>Unblock</Button>
                                ) : (
                                  <Button size="sm" variant="outline" className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white" onClick={() => blockUser(user.chat_id)} data-testid={`block-btn-${index}`}>Block</Button>
                                )}
                                {user.subscription_active && (
                                  <>
                                    <Button size="sm" variant="outline" className="border-[#0088CC] text-[#0088CC] hover:bg-[#0088CC] hover:text-white" onClick={() => viewReceiptHtml(user.chat_id)} data-testid={`receipt-view-${index}`}><FileText className="w-3.5 h-3.5 mr-1" />Receipt</Button>
                                    <Button size="sm" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white" onClick={() => downloadReceipt(user.chat_id)} data-testid={`receipt-pdf-${index}`}>PDF</Button>
                                    <Button size="sm" variant="outline" className="border-violet-600 text-violet-600 hover:bg-violet-600 hover:text-white" onClick={() => sendReceiptToUser(user.chat_id)} data-testid={`receipt-send-${index}`}><Send className="w-3.5 h-3.5 mr-1" />Send</Button>
                                  </>
                                )}
                                {user.subscription_active ? (
                                  <>
                                    <Button size="sm" variant="outline" onClick={() => extendSubscription(user.chat_id)}>Extend</Button>
                                    <Button size="sm" variant="destructive" onClick={() => deactivateSubscription(user.chat_id)}>Deactivate</Button>
                                  </>
                                ) : (
                                  <>
                                    <Button size="sm" className="btn-free" onClick={() => giveFreeSubscription(user.chat_id, 'monthly')}>Free</Button>
                                    <Button size="sm" onClick={() => activateSubscription(user.chat_id, 'monthly')}>+ Monthly</Button>
                                    <Button size="sm" variant="outline" onClick={() => activateSubscription(user.chat_id, 'yearly')}>+ Yearly</Button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
                <Card className="settings-card">
                  <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
                  <CardContent>
                    <div className="quick-action">
                      <label>Free/Extend Days:</label>
                      <Input type="number" value={extendDays} onChange={(e) => setExtendDays(parseInt(e.target.value) || 30)} min="1" max="365" />
                      <p className="form-hint">Used for "Free" and "Extend" buttons</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'billing' && (
              <div className="billing-container">
                <div className="billing-stats">
                  <Card className="billing-card"><CardHeader><CardDescription>Monthly Subscriptions</CardDescription><CardTitle className="billing-value">{billingData?.active_subscriptions?.monthly || 0}</CardTitle></CardHeader></Card>
                  <Card className="billing-card"><CardHeader><CardDescription>Yearly Subscriptions</CardDescription><CardTitle className="billing-value">{billingData?.active_subscriptions?.yearly || 0}</CardTitle></CardHeader></Card>
                  <Card className="billing-card"><CardHeader><CardDescription>This Month Revenue</CardDescription><CardTitle className="billing-value">{formatCurrency(billingData?.revenue?.this_month || 0)}</CardTitle></CardHeader></Card>
                  <Card className="billing-card"><CardHeader><CardDescription>Total Revenue</CardDescription><CardTitle className="billing-value">{formatCurrency(billingData?.revenue?.total || 0)}</CardTitle></CardHeader></Card>
                </div>
                <Card className="list-card">
                  <CardHeader>
                    <div className="card-header-flex">
                      <div><CardTitle>Payment History</CardTitle><CardDescription>Recent transactions</CardDescription></div>
                      <Button variant="outline" onClick={fetchData}>Refresh</Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="payments-scroll">
                      {payments.length === 0 ? <p className="empty-state">No payments yet</p> : (
                        <div className="payments-list">
                          {payments.map((payment, index) => (
                            <div key={payment.id || index} className="payment-item">
                              <div className="payment-info">
                                <span className="payment-id">#{payment.session_id?.slice(-8) || 'N/A'}</span>
                                <span className="payment-date">{formatDate(payment.created_at)}</span>
                              </div>
                              <div className="payment-details"><span>Chat ID: {payment.chat_id}</span><Badge>{payment.plan}</Badge></div>
                              <div className="payment-amount">
                                <span className="amount">{formatCurrency(payment.amount || 0)}</span>
                                <Badge className={payment.payment_status === 'paid' ? 'badge-success' : 'badge-warning'}>{payment.payment_status}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'affiliates' && (
              <div className="affiliates-container">
                <div className="affiliate-stats">
                  <Card className="affiliate-stat-card"><CardHeader><CardDescription>Total Affiliates</CardDescription><CardTitle className="stat-value-sm">{affiliateData.summary?.total_affiliates || 0}</CardTitle></CardHeader></Card>
                  <Card className="affiliate-stat-card"><CardHeader><CardDescription>Active Affiliates</CardDescription><CardTitle className="stat-value-sm">{affiliateData.summary?.active_affiliates || 0}</CardTitle></CardHeader></Card>
                  <Card className="affiliate-stat-card"><CardHeader><CardDescription>Total Referrals</CardDescription><CardTitle className="stat-value-sm">{affiliateData.summary?.total_referrals || 0}</CardTitle></CardHeader></Card>
                  <Card className="affiliate-stat-card"><CardHeader><CardDescription>Conversions</CardDescription><CardTitle className="stat-value-sm">{affiliateData.summary?.converted_referrals || 0}</CardTitle></CardHeader></Card>
                  <Card className="affiliate-stat-card"><CardHeader><CardDescription>Conversion Rate</CardDescription><CardTitle className="stat-value-sm">{affiliateData.summary?.conversion_rate || 0}%</CardTitle></CardHeader></Card>
                  <Card className="affiliate-stat-card highlight"><CardHeader><CardDescription>Pending Payouts</CardDescription><CardTitle className="stat-value-sm">{formatCurrency(affiliateData.summary?.pending_payouts || 0)}</CardTitle></CardHeader></Card>
                </div>
                <Card className="list-card">
                  <CardHeader>
                    <div className="card-header-flex">
                      <div><CardTitle>Affiliate Partners</CardTitle><CardDescription>Commission Rate: {affiliateData.summary?.commission_rate || 20}%</CardDescription></div>
                      <Button variant="outline" onClick={fetchData}>Refresh</Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="affiliate-list-scroll">
                      {affiliateData.affiliates.length === 0 ? <p className="empty-state">No affiliates yet</p> : (
                        <div className="affiliate-list">
                          {affiliateData.affiliates.map((affiliate, index) => (
                            <div key={affiliate.id || index} className="affiliate-item">
                              <div className="affiliate-info">
                                <div className="user-avatar">{(affiliate.first_name || affiliate.username || 'A')[0].toUpperCase()}</div>
                                <div className="affiliate-details">
                                  <p className="affiliate-name">{affiliate.first_name || affiliate.username || 'Unknown'}</p>
                                  <p className="affiliate-code">Code: <code>{affiliate.code}</code></p>
                                </div>
                              </div>
                              <div className="affiliate-stats-inline">
                                <div className="stat-item"><span className="stat-label">Referrals</span><span className="stat-num">{affiliate.total_referrals}</span></div>
                                <div className="stat-item"><span className="stat-label">Conversions</span><span className="stat-num">{affiliate.total_conversions}</span></div>
                                <div className="stat-item"><span className="stat-label">Total Earned</span><span className="stat-num">{formatCurrency(affiliate.total_earnings)}</span></div>
                              </div>
                              <div className="affiliate-payout">
                                <div className="pending-amount"><span className="payout-label">Pending</span><span className="payout-value">{formatCurrency(affiliate.pending_earnings)}</span></div>
                                {affiliate.pending_earnings > 0 && (
                                  <Button size="sm" onClick={() => processAffiliatePayout(affiliate.id, affiliate.pending_earnings)}>Pay Out</Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'users' && (
              <Card className="list-card">
                <CardHeader>
                  <div className="card-header-flex">
                    <div><CardTitle>All Users</CardTitle><CardDescription>Registered users with email addresses</CardDescription></div>
                    <Button variant="outline" onClick={fetchData}>Refresh</Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Support Desk: search by chat_id / assigned inbox / stripe payer email / username */}
                  <div className="input-group" style={{ marginBottom: 16, gap: 8, display: 'flex', flexWrap: 'wrap' }}>
                    <Input
                      data-testid="user-search-input"
                      placeholder="Search by chat_id, @telegramemailbot email, Stripe payer email, or username"
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') runUserSearch(); }}
                      style={{ minWidth: 320, flex: 1 }}
                    />
                    <Button data-testid="user-search-btn" onClick={runUserSearch} disabled={userSearchLoading}>
                      {userSearchLoading ? 'Searching…' : 'Search'}
                    </Button>
                    {userSearchResults !== null && (
                      <Button data-testid="user-search-clear-btn" variant="outline" onClick={clearUserSearch}>Clear</Button>
                    )}
                  </div>

                  {userSearchResults !== null && (
                    <div data-testid="user-search-results" style={{ marginBottom: 16 }}>
                      <h4 style={{ marginBottom: 8 }}>Search results ({userSearchResults.length})</h4>
                      {userSearchResults.length === 0 ? (
                        <p className="empty-state">No user found for &ldquo;{userSearchQuery}&rdquo;. If they paid via Stripe, ensure the email matches exactly.</p>
                      ) : (
                        <div className="user-list">
                          {userSearchResults.map((user, index) => (
                            <div key={`search-${user.chat_id || index}`} className="user-item" data-testid={`user-search-result-${index}`} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 12 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                                <div className="user-info">
                                  <div className="user-avatar">{(user.first_name || user.username || 'U')[0].toUpperCase()}</div>
                                  <div>
                                    <p className="user-name">
                                      {user.first_name || user.username || 'Unknown'}
                                      {user.username && <span style={{ marginLeft: 8, opacity: 0.7 }}>@{user.username}</span>}
                                    </p>
                                    <p className="user-email" data-testid={`user-search-result-email-${index}`}>
                                      {user.email_address}{' '}
                                      <button
                                        type="button"
                                        onClick={() => copyToClipboard(user.email_address)}
                                        style={{ marginLeft: 6, fontSize: 12, background: 'transparent', border: '1px solid #ccc', borderRadius: 4, padding: '1px 6px', cursor: 'pointer' }}
                                        data-testid={`user-search-copy-btn-${index}`}
                                      >
                                        Copy
                                      </button>
                                    </p>
                                    <p style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                                      chat_id: <strong>{user.chat_id}</strong>
                                      {user.payer_email && <> • paid by <strong>{user.payer_email}</strong></>}
                                      {user.is_blocked && <> • <span style={{ color: '#c00' }}>BLOCKED</span></>}
                                    </p>
                                  </div>
                                </div>
                                <div className="user-meta" style={{ alignItems: 'flex-end' }}>
                                  {(() => { const b = getPlanBadge(user); const usage = getStarterUsageLabel(user); return (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                                      <Badge className={b.className} data-testid={`user-search-result-badge-${index}`}>{b.label}</Badge>
                                      {usage && (
                                        <span style={{ fontSize: 12, color: '#93c5fd', fontWeight: 500, whiteSpace: 'nowrap' }} data-testid={`user-search-result-usage-${index}`}>{usage}</span>
                                      )}
                                    </div>
                                  ); })()}
                                  <span className="user-id">Emails: {user.email_count || 0}</span>
                                  <span className="user-date">{formatDate(user.created_at)}</span>
                                </div>
                              </div>

                              {/* Payment history — helps diagnose "why is this user Expired?" */}
                              {Array.isArray(user.payment_history) && user.payment_history.length > 0 && (
                                <div data-testid={`user-search-payment-history-${index}`} style={{ background: 'rgba(148, 163, 184, 0.08)', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 8, padding: 10 }}>
                                  <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, opacity: 0.85 }}>Payment History (last {user.payment_history.length})</p>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {user.payment_history.map((p, pi) => (
                                      <div key={pi} style={{ fontSize: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <span style={{ minWidth: 70, fontWeight: 500 }}>{p.plan || '—'}</span>
                                        <span>${(p.amount ?? 0).toFixed(2)}</span>
                                        <span style={{ color: p.payment_status === 'paid' ? '#4ade80' : '#f87171' }}>{p.payment_status || 'unknown'}</span>
                                        <span style={{ opacity: 0.7 }}>{p.subscription_activated ? 'activated' : 'NOT activated'}</span>
                                        <span style={{ opacity: 0.7 }}>{formatDate(p.updated_at || p.created_at)}</span>
                                        {p.payer_email && <span style={{ opacity: 0.7 }}>{p.payer_email}</span>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Support-desk action buttons */}
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={sendingTestFor === user.chat_id || !user.email_address}
                                  onClick={() => sendTestEmailTo(user)}
                                  data-testid={`user-search-send-test-btn-${index}`}
                                >
                                  {sendingTestFor === user.chat_id ? 'Sending…' : 'Send Test Email'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={adjustingFor === `${user.chat_id}:mark-starter`}
                                  onClick={() => markAsStarter(user)}
                                  data-testid={`user-search-mark-starter-btn-${index}`}
                                >
                                  {adjustingFor === `${user.chat_id}:mark-starter` ? 'Marking…' : 'Mark as Starter Paid'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={adjustingFor === `${user.chat_id}:grant-subscriber`}
                                  onClick={() => grantSubscriber(user)}
                                  data-testid={`user-search-grant-subscriber-btn-${index}`}
                                >
                                  {adjustingFor === `${user.chat_id}:grant-subscriber` ? 'Granting…' : 'Grant 1yr Subscriber'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={adjustingFor === `${user.chat_id}:extend-30`}
                                  onClick={() => extend30Days(user)}
                                  data-testid={`user-search-extend-30-btn-${index}`}
                                >
                                  {adjustingFor === `${user.chat_id}:extend-30` ? 'Extending…' : 'Extend +30 Days'}
                                </Button>
                                <Button
                                  size="sm"
                                  disabled={paymentLinkFor === user.chat_id}
                                  onClick={() => createPaymentLink(user)}
                                  data-testid={`user-search-payment-link-btn-${index}`}
                                >
                                  {paymentLinkFor === user.chat_id ? 'Creating…' : 'Payment Link ($14.99/yr)'}
                                </Button>
                              </div>
                              {paymentLinks[user.chat_id] && (
                                <div data-testid={`user-search-payment-link-${index}`} style={{ marginTop: 8, background: 'rgba(34,158,217,0.1)', border: '1px solid rgba(34,158,217,0.35)', borderRadius: 8, padding: 10 }}>
                                  <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#4db8ff' }}>Yearly payment link (send to customer):</p>
                                  <a href={paymentLinks[user.chat_id]} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#4db8ff', wordBreak: 'break-all' }}>{paymentLinks[user.chat_id]}</a>
                                  <div>
                                    <Button size="sm" variant="outline" style={{ marginTop: 8 }} onClick={() => { navigator.clipboard.writeText(paymentLinks[user.chat_id]); toast.success('Copied'); }} data-testid={`user-search-copy-payment-link-btn-${index}`}>Copy link</Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      <Separator className="my-3" />
                    </div>
                  )}

                  <ScrollArea className="user-list-scroll">
                    {getAllUsers().length === 0 ? <p className="empty-state">No users yet</p> : (
                      <div className="user-list">
                        {getAllUsers().map((user, index) => (
                          <div key={user.id || index} className="user-item" data-testid={`user-item-${index}`}>
                            <div className="user-info">
                              <div className="user-avatar">{(user.first_name || user.username || 'U')[0].toUpperCase()}</div>
                              <div>
                                <p className="user-name">{user.first_name || user.username || 'Unknown'}</p>
                                <p className="user-email">{user.email_address}</p>
                              </div>
                            </div>
                            <div className="user-meta">
                              {(() => { const b = getPlanBadge(user); const usage = getStarterUsageLabel(user); return (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                                  <Badge className={b.className}>{b.label}</Badge>
                                  {usage && <span style={{ fontSize: 12, color: '#93c5fd', fontWeight: 500, whiteSpace: 'nowrap' }}>{usage}</span>}
                                </div>
                              ); })()}
                              <span className="user-id">Emails: {user.email_count || 0}</span>
                              <span className="user-date">{formatDate(user.created_at)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            )}

            {activeTab === 'winback' && (
              <Card className="list-card" data-testid="winback-card">
                <div className="card-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <h2 style={{ margin: 0, color: '#f1f4f9' }}>Win-Back Campaign</h2>
                    <p style={{ margin: '4px 0 0', color: '#b6bfce', fontSize: 14 }}>
                      Lapsed users who never bought a plan. Nudge them to unlock ${'{'}14.99{'}'} storage.
                    </p>
                  </div>
                  <Button onClick={fetchWinBack} disabled={winBackLoading} data-testid="winback-refresh-btn">
                    {winBackLoading ? 'Loading…' : 'Refresh'}
                  </Button>
                  <Button onClick={nudgeAll} disabled={nudgingFor === 'ALL'} style={{ marginLeft: 8 }} data-testid="winback-nudge-all-btn">
                    {nudgingFor === 'ALL' ? 'Sending…' : 'Nudge All'}
                  </Button>
                </div>

                {winBack && (
                  <div style={{ display: 'flex', gap: 24, marginBottom: 16 }} data-testid="winback-stats">
                    <div><strong style={{ fontSize: 22, color: '#f1f4f9' }}>{winBack.total}</strong><div style={{ color: '#b6bfce', fontSize: 13 }}>Lapsed users</div></div>
                    <div><strong style={{ fontSize: 22, color: '#f1f4f9' }}>{winBack.with_activity}</strong><div style={{ color: '#b6bfce', fontSize: 13 }}>Had activity</div></div>
                  </div>
                )}

                {winBackLoading && !winBack ? (
                  <p>Loading…</p>
                ) : winBack && winBack.users.length > 0 ? (
                  <div className="table-wrap" style={{ overflowX: 'auto' }}>
                    <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', color: '#e6e9ef' }} data-testid="winback-table">
                      <thead>
                        <tr style={{ textAlign: 'left', borderBottom: '1px solid #2a3142', color: '#f1f4f9' }}>
                          <th style={{ padding: '8px' }}>Email</th>
                          <th style={{ padding: '8px' }}>Telegram User</th>
                          <th style={{ padding: '8px' }}>Emails Received</th>
                          <th style={{ padding: '8px' }}>Type</th>
                          <th style={{ padding: '8px' }}>Chat</th>
                          <th style={{ padding: '8px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {winBack.users.map((u) => (
                          <tr key={u.chat_id} style={{ borderBottom: '1px solid #1e2430' }} data-testid={`winback-row-${u.chat_id}`}>
                            <td style={{ padding: '8px', fontFamily: 'monospace', color: '#e6e9ef' }}>{u.email_address}</td>
                            <td style={{ padding: '8px', color: '#e6e9ef' }} data-testid={`winback-username-${u.chat_id}`}>
                              {u.username ? (
                                <a
                                  href={`https://t.me/${u.username}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#4db8ff', textDecoration: 'none' }}
                                >
                                  @{u.username}
                                </a>
                              ) : u.first_name ? (
                                <span>{u.first_name}</span>
                              ) : (
                                <span style={{ color: '#8a94a6' }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '8px', color: '#e6e9ef' }}>{u.emails_received_count}</td>
                            <td style={{ padding: '8px', color: '#e6e9ef' }}>{u.is_group ? 'Group' : 'DM'} · {u.plan_type}</td>
                            <td style={{ padding: '8px', color: '#b6bfce' }}>{u.chat_id}</td>
                            <td style={{ padding: '8px' }}>
                              <Button
                                size="sm"
                                disabled={nudgingFor === u.chat_id}
                                onClick={() => sendWinBackNudge(u.chat_id)}
                                data-testid={`winback-nudge-btn-${u.chat_id}`}
                              >
                                {nudgingFor === u.chat_id ? 'Sending…' : (u.nudged_at ? 'Nudge again' : 'Send Nudge')}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : winBack ? (
                  <p data-testid="winback-empty">No lapsed users found. 🎉</p>
                ) : null}
              </Card>
            )}


            {activeTab === 'revenue' && (
              <Card className="list-card" data-testid="revenue-card">
                <div className="card-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div>
                    <h2 style={{ margin: 0, color: '#f1f4f9' }}>Revenue Diagnostic</h2>
                    <p style={{ margin: '4px 0 0', color: '#b6bfce', fontSize: 14 }}>Where your funnel converts — and where money is leaking.</p>
                  </div>
                  <Button onClick={fetchRevenue} disabled={revenueLoading} data-testid="revenue-refresh-btn">
                    {revenueLoading ? 'Loading…' : 'Refresh'}
                  </Button>
                </div>

                {revenue ? (
                  <div style={{ color: '#e6e9ef' }}>
                    {/* Funnel */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24 }} data-testid="revenue-funnel">
                      {[
                        { label: 'Total signups', value: revenue.funnel.total_users, color: '#f1f4f9' },
                        { label: 'Paid users', value: revenue.funnel.paid_users, color: '#22c55e' },
                        { label: 'Unpaid users', value: revenue.funnel.unpaid_users, color: '#f59e0b' },
                        { label: 'Conversion', value: `${revenue.funnel.conversion_rate_pct}%`, color: '#4db8ff' },
                      ].map((s) => (
                        <div key={s.label} style={{ background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 12, padding: '16px 20px', minWidth: 140 }}>
                          <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</div>
                          <div style={{ fontSize: 13, color: '#b6bfce' }}>{s.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Revenue */}
                    <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 12, padding: 16, marginBottom: 20 }} data-testid="revenue-collected">
                      <div style={{ fontSize: 13, color: '#b6bfce', marginBottom: 4 }}>Total revenue collected (paid transactions)</div>
                      <div style={{ fontSize: 30, fontWeight: 700, color: '#22c55e' }}>${revenue.revenue.total_collected}</div>
                      <div style={{ fontSize: 13, color: '#b6bfce', marginTop: 8 }}>
                        {revenue.revenue.paid_transactions} paid · {revenue.revenue.pending_checkouts} abandoned/pending checkouts
                        {Object.keys(revenue.revenue.by_plan || {}).length > 0 && (
                          <span> · {Object.entries(revenue.revenue.by_plan).map(([p, v]) => `${p}: $${v.amount} (${v.count})`).join(' · ')}</span>
                        )}
                      </div>
                    </div>

                    {revenue.countdown && <CountdownReport data={revenue.countdown} />}

                    {/* Leaks */}
                    <h3 style={{ color: '#f1f4f9', margin: '8px 0 12px', fontSize: 16 }}>💸 Money leaks</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }} data-testid="revenue-leaks">
                      <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 10, padding: 14 }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#f59e0b' }}>{revenue.leaks.received_emails_but_never_paid}</div>
                        <div style={{ fontSize: 13, color: '#e6e9ef' }}>Got value but never paid</div>
                        <div style={{ fontSize: 12, color: '#b6bfce', marginTop: 4 }}>Users who received emails but aren't on a paid plan. Prime win-back targets.</div>
                      </div>
                      <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 10, padding: 14 }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444' }}>{revenue.leaks.paid_but_not_activated_count}</div>
                        <div style={{ fontSize: 13, color: '#e6e9ef' }}>Paid but NOT activated</div>
                        <div style={{ fontSize: 12, color: '#b6bfce', marginTop: 4 }}>Stripe charged them but the subscription never turned on. Fix these first — real lost customers.</div>
                      </div>
                      <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 10, padding: 14 }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#ef4444' }}>{revenue.leaks.paid_but_mapping_still_unpaid}</div>
                        <div style={{ fontSize: 13, color: '#e6e9ef' }}>Paid but still locked</div>
                        <div style={{ fontSize: 12, color: '#b6bfce', marginTop: 4 }}>Mailboxes of paying users that are on an unpaid plan and can't receive emails. Covered mailboxes are not counted.</div>
                      </div>
                    </div>

                    <PaidLockedTable
                      rows={revenue.leaks.paid_but_locked}
                      onFix={async (email) => {
                        await authAxios().post(`/api/admin/mailbox/fix-starter?email=${encodeURIComponent(email)}`);
                        fetchRevenue();
                      }}
                    />

                    {revenue.leaks.paid_but_not_activated.length > 0 && (
                      <div style={{ marginTop: 20 }}>
                        <h4 style={{ color: '#f1f4f9', fontSize: 14, marginBottom: 8 }}>Paid-but-not-activated transactions</h4>
                        <div className="table-wrap" style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', color: '#e6e9ef', fontSize: 13 }} data-testid="revenue-leak-table">
                            <thead>
                              <tr style={{ textAlign: 'left', borderBottom: '1px solid #2a3142', color: '#f1f4f9' }}>
                                <th style={{ padding: 8 }}>Chat ID</th>
                                <th style={{ padding: 8 }}>Plan</th>
                                <th style={{ padding: 8 }}>Amount</th>
                                <th style={{ padding: 8 }}>Payer email</th>
                                <th style={{ padding: 8 }}>Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {revenue.leaks.paid_but_not_activated.map((t, i) => (
                                <tr key={t.session_id || i} style={{ borderBottom: '1px solid #1e2430' }}>
                                  <td style={{ padding: 8 }}>{t.chat_id}</td>
                                  <td style={{ padding: 8 }}>{t.plan}</td>
                                  <td style={{ padding: 8 }}>${t.amount}</td>
                                  <td style={{ padding: 8 }}>{t.payer_email || '—'}</td>
                                  <td style={{ padding: 8, color: '#b6bfce' }}>{formatDate(t.created_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ color: '#b6bfce' }}>Loading…</p>
                )}
              </Card>
            )}

            {activeTab === 'emails' && (
              <Card className="list-card">
                <CardHeader>
                  <div className="card-header-flex">
                    <div><CardTitle>Email Logs</CardTitle><CardDescription>Recent email activity</CardDescription></div>
                    <Button variant="outline" onClick={clearEmailSearch}>Refresh</Button>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <Input
                      placeholder="Search emails by address or subject…"
                      value={emailSearchQuery}
                      onChange={(e) => setEmailSearchQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') runEmailSearch(); }}
                      data-testid="email-search-input"
                    />
                    <Button onClick={runEmailSearch} disabled={emailSearchLoading} data-testid="email-search-btn">
                      {emailSearchLoading ? 'Searching…' : 'Search'}
                    </Button>
                    {emailSearchActive && (
                      <Button variant="outline" onClick={clearEmailSearch} data-testid="email-search-clear-btn">Clear</Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="email-list-scroll">
                    {emails.length === 0 ? <p className="empty-state" style={{ color: '#b6bfce' }}>{emailSearchActive ? 'No emails match your search.' : 'No emails yet'}</p> : (
                      <div className="email-list">
                        {emails.map((email, index) => (
                          <div key={email.id || index} className="email-item" data-testid={`email-item-${index}`}>
                            <div className="email-header">
                              <Badge variant={email.direction === 'inbound' ? 'default' : 'secondary'}>{email.direction === 'inbound' ? 'Received' : 'Sent'}</Badge>
                              <span className="email-date" style={{ color: '#b6bfce' }}>{formatDate(email.timestamp)}</span>
                            </div>
                            <div className="email-details" style={{ color: '#e6e9ef' }}>
                              <p style={{ color: '#e6e9ef' }}><strong style={{ color: '#f1f4f9' }}>From:</strong> {email.from_email}</p>
                              <p style={{ color: '#e6e9ef' }}><strong style={{ color: '#f1f4f9' }}>To:</strong> {email.to_email}</p>
                              <p style={{ color: '#e6e9ef' }}><strong style={{ color: '#f1f4f9' }}>Subject:</strong> {email.subject}</p>
                            </div>
                            {index < emails.length - 1 && <Separator className="my-3" />}
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            )}

            {activeTab === 'setup' && (
              <div className="setup-container">
                <Card className="setup-card">
                  <CardHeader><CardTitle>Gmail Integration</CardTitle><CardDescription>Connect Gmail to receive forwarded emails</CardDescription></CardHeader>
                  <CardContent>
                    <div className="gmail-status">
                      {gmailStatus?.connected ? (
                        <div className="connected-status">
                          <div className="status-indicator success"></div>
                          <div><p><strong>Connected:</strong> {gmailStatus.email}</p><p className="text-muted">Connected at: {formatDate(gmailStatus.connected_at)}</p></div>
                        </div>
                      ) : (
                        <div className="connected-status"><div className="status-indicator pending"></div><p>Gmail not connected</p></div>
                      )}
                    </div>
                    <div className="button-group">
                      {gmailStatus?.connected ? (
                        <><Button onClick={triggerPoll} variant="outline">Poll Now</Button><Button onClick={disconnectGmail} variant="destructive">Disconnect</Button></>
                      ) : (
                        <Button onClick={connectGmail}>Connect Gmail</Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
                <Card className="setup-card">
                  <CardHeader><CardTitle>Telegram Webhook</CardTitle><CardDescription>Configure the webhook URL</CardDescription></CardHeader>
                  <CardContent>
                    <div className="webhook-status">
                      <h4>Current Status:</h4>
                      {webhookInfo?.bot1?.result ? (
                        <div className="webhook-info">
                          <p><strong>URL:</strong> {webhookInfo.bot1.result.url || 'Not set'}</p>
                          <p><strong>Pending:</strong> {webhookInfo.bot1.result.pending_update_count || 0}</p>
                        </div>
                      ) : <p>No webhook info</p>}
                    </div>
                    <Separator className="my-4" />
                    <div className="webhook-form">
                      <p className="form-hint">Webhook URL: <code>{API_URL}/api/telegram/webhook</code></p>
                      <div className="input-group">
                        <Input placeholder="https://your-domain.com/api/telegram/webhook" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} />
                        <Button onClick={setWebhook}>Set Webhook</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </main>
      <footer className="app-footer"><p>Instant Email Bot Admin - Powered by Telegram, Brevo & Gmail</p></footer>
    </div>
  );
}

export default function AdminDashboard() {
  const [authed, setAuthed] = useState(null); // null = checking, false = not authed, object = authed

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { setAuthed(false); return; }
    axios.get(`${API_URL}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setAuthed(res.data))
      .catch(() => { localStorage.removeItem('admin_token'); setAuthed(false); });
  }, []);

  if (authed === null) {
    return <div className="min-h-screen bg-gray-950 flex items-center justify-center"><div className="loading-spinner"></div></div>;
  }

  if (!authed) {
    return <AdminLogin onLogin={(data) => setAuthed(data)} />;
  }

  return <Dashboard onLogout={() => setAuthed(false)} />;
}
